import React from 'react'
import { Shield, Package, Settings, AlertCircle } from 'lucide-react'
import { EventFormData } from '../EventCreationWizard'
import toast from 'react-hot-toast'
import { EnterpriseToast } from '../../ui/EnterpriseToast'

interface RulesSettingsStepProps {
  formData: EventFormData
  updateFormData: (updates: Partial<EventFormData>) => void
  onNext: () => void
  onPrev: () => void
  isFirstStep: boolean
  isLastStep: boolean
}

const RulesSettingsStep: React.FC<RulesSettingsStepProps> = ({
  formData,
  updateFormData,
  onNext,
  onPrev
}) => {
  const handleNext = () => {
    // Validate: if max_orders is set, it should be greater than 0
    if (formData.max_orders !== null && formData.max_orders <= 0) {
      toast.custom((t) => (
        <EnterpriseToast
          t={t}
          type="error"
          title="Invalid Max Orders"
          message="Max orders must be greater than 0 or set to unlimited"
        />
      ))
      return
    }

    // Validate: if pre_order_limit is set, it should be greater than 0
    if (formData.pre_order_limit !== null && formData.pre_order_limit <= 0) {
      toast.custom((t) => (
        <EnterpriseToast
          t={t}
          type="error"
          title="Invalid Pre-Order Limit"
          message="Pre-order limit must be greater than 0 or set to unlimited"
        />
      ))
      return
    }

    // Phase 2: Validate lobster tail capacity limits
    if (formData.max_lobster_tails !== null && formData.max_lobster_tails <= 0) {
      toast.custom((t) => (
        <EnterpriseToast
          t={t}
          type="error"
          title="Invalid Lobster Tail Limit"
          message="Max lobster tails must be greater than 0 or set to unlimited"
        />
      ))
      return
    }

    if (formData.max_tails_per_order !== null && formData.max_tails_per_order <= 0) {
      toast.custom((t) => (
        <EnterpriseToast
          t={t}
          type="error"
          title="Invalid Tails Per Order"
          message="Tails per order limit must be greater than 0 or set to unlimited"
        />
      ))
      return
    }

    onNext()
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-foreground mb-2">Rules & Settings</h2>
        <p className="text-muted-foreground">
          Configure access controls and event parameters
        </p>
      </div>

      {/* Event Status */}
      <div className="bg-muted/30 border border-border rounded-lg p-6">
        <div className="flex items-center space-x-2 mb-4">
          <Settings className="text-primary" size={20} />
          <h3 className="text-lg font-semibold text-foreground">Event Status</h3>
        </div>

        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Choose whether to publish this event immediately or save it as a draft.
          </p>

          {/* Segmented Control for Status */}
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => updateFormData({ status: 'draft' })}
              className={`relative p-4 rounded-lg border-2 transition-all ${
                formData.status === 'draft'
                  ? 'border-primary bg-primary/10'
                  : 'border-border bg-card hover:border-primary/50'
              }`}
            >
              <div className="flex flex-col items-start space-y-1">
                <div className="flex items-center space-x-2">
                  <div className={`w-3 h-3 rounded-full ${formData.status === 'draft' ? 'bg-primary' : 'bg-muted-foreground'}`} />
                  <span className="menu-item-name font-semibold">Draft</span>
                </div>
                <p className="text-xs text-left" style={{ color: '#6B7280' }}>
                  Save for later. Event will not be visible to anyone.
                </p>
              </div>
              {formData.status === 'draft' && (
                <div className="absolute top-2 right-2">
                  <Shield className="text-primary" size={16} />
                </div>
              )}
            </button>

            <button
              onClick={() => updateFormData({ status: 'scheduled' })}
              className={`relative p-4 rounded-lg border-2 transition-all ${
                formData.status === 'scheduled'
                  ? 'border-green-500 bg-green-500/10'
                  : 'border-border bg-card hover:border-green-500/50'
              }`}
            >
              <div className="flex flex-col items-start space-y-1">
                <div className="flex items-center space-x-2">
                  <div className={`w-3 h-3 rounded-full ${formData.status === 'scheduled' ? 'bg-green-500' : 'bg-muted-foreground'}`} />
                  <span className="menu-item-name font-semibold">Scheduled</span>
                </div>
                <p className="text-xs text-left" style={{ color: '#6B7280' }}>
                  Publish event. Will be visible to SECRET members.
                </p>
              </div>
              {formData.status === 'scheduled' && (
                <div className="absolute top-2 right-2">
                  <Shield className="text-green-500" size={16} />
                </div>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Order Limits */}
      <div className="bg-muted/30 border border-border rounded-lg p-6">
        <div className="flex items-center space-x-2 mb-4">
          <Package className="text-primary" size={20} />
          <h3 className="text-lg font-semibold text-foreground">Order Limits</h3>
        </div>

        <div className="space-y-4">
          {/* Max Total Orders */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Maximum Total Orders
            </label>
            <div className="flex items-center space-x-3">
              <input
                type="number"
                min="1"
                value={formData.max_orders || ''}
                onChange={(e) => updateFormData({ max_orders: parseInt(e.target.value) || null })}
                placeholder="Unlimited"
                className="flex-1 px-4 py-2 bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <button
                onClick={() => updateFormData({ max_orders: null })}
                className={`px-4 py-2 rounded-md transition-colors ${
                  formData.max_orders === null
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-card border border-border hover:bg-muted'
                }`}
              >
                ∞ Unlimited
              </button>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              The total number of orders allowed for this event. Leave unlimited if you don't want to restrict total orders.
            </p>
            {formData.max_orders !== null && (
              <div className="flex items-center space-x-1 mt-2 text-primary">
                <AlertCircle size={14} />
                <span className="text-xs">Event will close automatically after {formData.max_orders} orders</span>
              </div>
            )}
          </div>

          {/* Pre-Order Limit per Customer */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Items per Order Limit
            </label>
            <div className="flex items-center space-x-3">
              <input
                type="number"
                min="1"
                value={formData.pre_order_limit || ''}
                onChange={(e) => updateFormData({ pre_order_limit: parseInt(e.target.value) || null })}
                placeholder="Unlimited"
                className="flex-1 px-4 py-2 bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <button
                onClick={() => updateFormData({ pre_order_limit: null })}
                className={`px-4 py-2 rounded-md transition-colors ${
                  formData.pre_order_limit === null
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-card border border-border hover:bg-muted'
                }`}
              >
                ∞ Unlimited
              </button>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              The maximum number of items a single customer can order. Leave unlimited to allow any quantity.
            </p>
            {formData.pre_order_limit !== null && (
              <div className="flex items-center space-x-1 mt-2 text-primary">
                <AlertCircle size={14} />
                <span className="text-xs">Each customer can order up to {formData.pre_order_limit} total items</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Lobster Tail Capacity Limits */}
      <div className="bg-muted/30 border border-border rounded-lg p-6">
        <div className="flex items-center space-x-2 mb-4">
          <Package className="text-primary" size={20} />
          <h3 className="text-lg font-semibold text-foreground">Lobster Tail Capacity</h3>
        </div>

        <div className="space-y-4">
          {/* Max Total Lobster Tails */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Maximum Total Lobster Tails
            </label>
            <div className="flex items-center space-x-3">
              <input
                type="number"
                min="1"
                value={formData.max_lobster_tails || ''}
                onChange={(e) => updateFormData({ max_lobster_tails: parseInt(e.target.value) || null })}
                placeholder="Unlimited"
                className="flex-1 px-4 py-2 bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <button
                onClick={() => updateFormData({ max_lobster_tails: null })}
                className={`px-4 py-2 rounded-md transition-colors ${
                  formData.max_lobster_tails === null
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-card border border-border hover:bg-muted'
                }`}
              >
                ∞ Unlimited
              </button>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              The total number of lobster tails available for this event across all menu items.
            </p>
            {formData.max_lobster_tails !== null && (
              <div className="flex items-center space-x-1 mt-2 text-primary">
                <AlertCircle size={14} />
                <span className="text-xs">🦞 Event will stop accepting orders when {formData.max_lobster_tails} tails are ordered</span>
              </div>
            )}
          </div>

          {/* Max Tails per Order */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Lobster Tails per Order Limit
            </label>
            <div className="flex items-center space-x-3">
              <input
                type="number"
                min="1"
                value={formData.max_tails_per_order || ''}
                onChange={(e) => updateFormData({ max_tails_per_order: parseInt(e.target.value) || null })}
                placeholder="Unlimited"
                className="flex-1 px-4 py-2 bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <button
                onClick={() => updateFormData({ max_tails_per_order: null })}
                className={`px-4 py-2 rounded-md transition-colors ${
                  formData.max_tails_per_order === null
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-card border border-border hover:bg-muted'
                }`}
              >
                ∞ Unlimited
              </button>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              The maximum number of lobster tails a single customer can order in one transaction.
            </p>
            {formData.max_tails_per_order !== null && (
              <div className="flex items-center space-x-1 mt-2 text-primary">
                <AlertCircle size={14} />
                <span className="text-xs">🦞 Each customer can order up to {formData.max_tails_per_order} lobster tails total</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Summary Card */}
      <div className="bg-card border border-primary/30 rounded-lg p-4 config-summary">
        <h4 className="text-sm font-semibold mb-2">Configuration Summary</h4>
        <div className="space-y-1 text-xs">
          <p>• Status: <span className="font-medium">{formData.status === 'draft' ? 'Draft (Not Published)' : 'Scheduled (Will Publish)'}</span></p>
          <p>• Visibility: <span className="font-medium">{formData.visibility === 'members_only' ? 'SECRET Members Only' : 'Public'}</span></p>
          <p>• Max Orders: <span className="font-medium">{formData.max_orders || 'Unlimited'}</span></p>
          <p>• Items per Order: <span className="font-medium">{formData.pre_order_limit || 'Unlimited'}</span></p>
          <p>• Max Lobster Tails: <span className="font-medium">{formData.max_lobster_tails || 'Unlimited'}</span></p>
          <p>• Tails per Order: <span className="font-medium">{formData.max_tails_per_order || 'Unlimited'}</span></p>
        </div>
      </div>
    </div>
  )
}

export default RulesSettingsStep
